import { useState, useEffect, useCallback } from 'react';
import { ContactListCard } from '../ContactListCard';
import { ContactDetailDrawer } from '../ContactDetailDrawer';
import { Drawer } from '../Drawer';
import { ReviewQueuePanel } from '../ReviewQueuePanel';
import { SemanticSearchResults } from '../SemanticSearchResults';
import { contactsAPI, draftsAPI, nudgesAPI, searchAPI, tagsAPI } from '../../api/client';
import type { ClawerContact, Draft, Nudge, SemanticSearchResult, Tag } from '../../types';

export function PeopleScreen() {
  const [contacts, setContacts] = useState<ClawerContact[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState<ClawerContact | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTagLabel, setActiveTagLabel] = useState<string | null>(null);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [semanticResults, setSemanticResults] = useState<SemanticSearchResult[]>([]);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [semanticError, setSemanticError] = useState<string | null>(null);
  const [nudgesByContact, setNudgesByContact] = useState<Record<number, Nudge>>({});
  const [latestDraftByContact, setLatestDraftByContact] = useState<Record<number, Draft>>({});

  // Fetch tags for filter chips
  useEffect(() => {
    tagsAPI.list().then(setAllTags).catch(console.error);
  }, []);

  const fetchNudges = useCallback(async () => {
    try {
      const rows = await nudgesAPI.list({ status: 'active' });
      const byContact: Record<number, Nudge> = {};
      rows.forEach((row) => {
        if (!byContact[row.contact_id]) byContact[row.contact_id] = row;
      });
      setNudgesByContact(byContact);
    } catch (err) {
      console.error('Failed to load nudges:', err);
    }
  }, []);

  // Fetch contacts (server-side search + tag filter)
  const fetchContacts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params: { q?: string; tag?: string; limit: number } = { limit: 100 };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (activeTagLabel) params.tag = activeTagLabel;
      const data = await contactsAPI.list(params);
      setContacts(data);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError('Failed to load contacts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, activeTagLabel]);

  // Debounced fetch on search/tag change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchContacts();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchContacts]);

  useEffect(() => {
    void fetchNudges();
  }, [fetchNudges]);

  useEffect(() => {
    async function fetchLatestDrafts() {
      const entries = await Promise.all(
        contacts.slice(0, 20).map(async (contact) => {
          try {
            const draft = await draftsAPI.latest(contact.id);
            return [contact.id, draft] as const;
          } catch {
            return null;
          }
        }),
      );
      const next: Record<number, Draft> = {};
      entries.forEach((entry) => {
        if (entry) next[entry[0]] = entry[1];
      });
      setLatestDraftByContact(next);
    }
    if (contacts.length > 0) {
      void fetchLatestDrafts();
    }
  }, [contacts]);

  const handleContactClick = (contact: ClawerContact) => {
    setSelectedContact(contact);
  };

  const handleDrawerClose = () => {
    setSelectedContact(null);
  };

  const handleTagsChanged = (updatedContact: ClawerContact) => {
    setContacts(prev => prev.map(c => c.id === updatedContact.id ? updatedContact : c));
    if (selectedContact?.id === updatedContact.id) {
      setSelectedContact(updatedContact);
    }
  };

  const handleTrustLevelChanged = (updatedContact: ClawerContact) => {
    setContacts(prev => prev.map(c => c.id === updatedContact.id ? updatedContact : c));
    if (selectedContact?.id === updatedContact.id) {
      setSelectedContact(updatedContact);
    }
  };

  const toggleTagFilter = (label: string) => {
    setActiveTagLabel(prev => prev === label ? null : label);
  };

  // Semantic search (query -> /api/search), debounced and shown alongside contact list.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3) {
      setSemanticResults([]);
      setSemanticLoading(false);
      setSemanticError(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSemanticLoading(true);
        setSemanticError(null);
        const rows = await searchAPI.list({ q, limit: 10 });
        setSemanticResults(rows);
      } catch (err) {
        console.error('Failed to fetch semantic results:', err);
        setSemanticError('Semantic search failed. Please try again.');
      } finally {
        setSemanticLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleOpenContactFromSemantic = useCallback(async (contactId: number) => {
    const existing = contacts.find(c => c.id === contactId);
    if (existing) {
      setSelectedContact(existing);
      return;
    }

    try {
      const full = await contactsAPI.get(contactId);
      setSelectedContact(full);
    } catch (err) {
      console.error('Failed to open contact from semantic result:', err);
      setError('Failed to open selected contact.');
    }
  }, [contacts]);

  async function handleSnoozeNudge(nudgeId: string) {
    try {
      await nudgesAPI.snooze(nudgeId, '3d');
      await fetchNudges();
    } catch (err) {
      console.error('Failed to snooze nudge:', err);
    }
  }

  async function handleDismissNudge(nudgeId: string) {
    try {
      await nudgesAPI.dismiss(nudgeId);
      await fetchNudges();
    } catch (err) {
      console.error('Failed to dismiss nudge:', err);
    }
  }

  return (
    <div className="flex flex-col h-full max-w-md sm:max-w-lg md:max-w-2xl mx-auto w-full">
      {/* Header with search */}
      <div className="px-4 pt-4 pb-3 border-b border-edge/50 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="relative">
          <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary text-xl">
            search
          </span>
          <input
            type="text"
            placeholder="Search contacts, phone, summary..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-edge focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 transition-all text-ink placeholder-ink-tertiary bg-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-canvas-secondary rounded-full transition-colors"
              aria-label="Clear search"
            >
              <span className="material-icons-round text-ink-tertiary text-lg">close</span>
            </button>
          )}
        </div>

        {/* Search results count */}
        {searchQuery && !isLoading && (
          <p className="text-xs text-ink-secondary mt-2">
            {contacts.length} {contacts.length === 1 ? 'result' : 'results'}
          </p>
        )}
      </div>

      {/* Tag filter chips */}
      {allTags.length > 0 && (
        <div className="flex gap-2 px-4 py-2 overflow-x-auto no-scrollbar border-b border-edge/30">
          {allTags.map((tag) => {
            const isActive = activeTagLabel === tag.label;
            return (
              <button
                key={tag.id}
                onClick={() => toggleTagFilter(tag.label)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isActive ? 'ring-2 ring-offset-1' : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: isActive ? tag.color : tag.color + '20',
                  color: isActive ? 'white' : tag.color,
                  ['--tw-ring-color' as string]: tag.color,
                }}
              >
                {tag.icon && <span className="mr-1">{tag.icon}</span>}
                {tag.label}
              </button>
            );
          })}
          {activeTagLabel && (
            <button
              onClick={() => setActiveTagLabel(null)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium bg-canvas border border-edge text-ink-secondary hover:bg-canvas-secondary transition-colors"
              data-ph-no-mask
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* Contact list */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-20 glass-scroll">
        <div className="mb-4">
          <ReviewQueuePanel compact onChanged={() => { void fetchContacts(); }} />
        </div>

        <SemanticSearchResults
          query={searchQuery}
          results={semanticResults}
          isLoading={semanticLoading}
          error={semanticError}
          onOpenContact={handleOpenContactFromSemantic}
        />

        {/* Loading state */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-edge border-t-ink-secondary rounded-full animate-spin mb-4" />
            <p className="text-ink-secondary" data-ph-no-mask>Loading contacts...</p>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <span className="material-icons-round text-red-400 text-5xl mb-3">error_outline</span>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchContacts}
              className="px-4 py-2 bg-ink text-white rounded-lg hover:opacity-90 transition-colors"
              data-ph-no-mask
            >
              Retry
            </button>
          </div>
        )}

        {/* Contact list */}
        {!isLoading && !error && contacts.length > 0 && (
          <>
            {contacts.map((contact) => (
              <ContactListCard
                key={contact.id}
                contact={contact}
                nudge={nudgesByContact[contact.id]}
                onSnoozeNudge={handleSnoozeNudge}
                onDismissNudge={handleDismissNudge}
                onClick={() => handleContactClick(contact)}
              />
            ))}
          </>
        )}

        {/* Empty state - no contacts */}
        {!isLoading && !error && contacts.length === 0 && !searchQuery && !activeTagLabel && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <span className="material-icons-round text-ink-tertiary text-6xl mb-4">people_outline</span>
            <h3 className="text-xl font-bold text-ink mb-2" data-ph-no-mask>No contacts yet</h3>
            <p className="text-ink-secondary max-w-sm" data-ph-no-mask>
              Your contacts will appear here once WhatsApp messages start syncing.
            </p>
          </div>
        )}

        {/* Empty state - no search results */}
        {!isLoading && !error && contacts.length === 0 && (searchQuery || activeTagLabel) && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <span className="material-icons-round text-ink-tertiary text-6xl mb-4">search_off</span>
            <h3 className="text-xl font-bold text-ink mb-2" data-ph-no-mask>No matches found</h3>
            <p className="text-ink-secondary max-w-sm" data-ph-no-mask>
              Try different keywords or clear your filter.
            </p>
          </div>
        )}
      </div>

      {/* Contact detail drawer */}
      <Drawer
        isOpen={selectedContact !== null}
        onClose={handleDrawerClose}
        fullBleed
      >
        {selectedContact && (
          <ContactDetailDrawer
            contact={selectedContact}
            nudge={nudgesByContact[selectedContact.id] || null}
            latestDraft={latestDraftByContact[selectedContact.id] || null}
            onTagsChanged={handleTagsChanged}
            onTrustLevelChanged={handleTrustLevelChanged}
          />
        )}
      </Drawer>
    </div>
  );
}

export default PeopleScreen;
